<template>
  <el-row :gutter="20">
    <el-col :span="4">
      <span class="placeholder"></span>
    </el-col>
    <el-col
      :span="17"
      :offset="2"
    >
      <div class="infoContent">
        <div
          class="progressWrapper"
          v-if="status === 'connecting'"
        >
          <progress-bar :done="status !== 'connecting'" />
          <p class="textCenter">{{ $t('urlCheck.connecting', { url }) }}</p>
        </div>
        <div
          class="successWrapper"
          v-if="status === 'success'"
        >
          <div class="successContent">
            <i class="el-icon-success successIcon"></i>
            <span>{{ $t('urlCheck.connectionSuccess') }}</span>
          </div>
          <div class="successTip">{{ $t('urlCheck.successTip') }}</div>
          <div class="imageWrapper">
            <el-card style="width: 800px;height: 300px;">
              <el-image
                :src="image"
                :preview-src-list="[image]"
              />
            </el-card>
          </div>
        </div>
        <div
          class="errorWrapper"
          v-if="status === 'error' || status === 'checking' || status === 'checked'"
        >
          <div class="errorContent">
            <template v-if="!skipScreenShot">
              <p>
                <i class="el-icon-error errorIcon"></i> {{ $t('urlCheck.unableToConnect') }}
              </p>
            </template>
            <template v-else>
              <p>
                <i
                  class="el-icon-error errorIcon"
                  v-if="status === 'error'"
                />
                <i
                  :class="['el-icon-success checking', {successIcon: status === 'checked'}]"
                  v-else
                />
                {{ status === 'error' ? $t('urlCheck.unableToConnectShort') : 
                   status === 'checked' ? $t('urlCheck.connectionSuccessful') : 
                   $t('urlCheck.javaArchiveChecking') }}
              </p>
            </template>
            <div class="diagList">
              <div
                v-for="(stage, index) in stageList"
                :key="stage.stageName"
              >
                <i :class="{
                        stageIcon: true,
                        ['el-icon-loading']: nextStage === index,
                        ['el-icon-success']: stage.status !== 'FAIL' && nextStage !== index,
                        successIcon: stage.status === 'PASS',
                         ['el-icon-error']: stage.status === 'FAIL',
                          errorIcon:  stage.status === 'FAIL'}">
                </i>
                <span>
                  {{stage.resultInfo ?? $t(stage.description) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="['success', 'error', 'checking', 'checked'].includes(status)">
          <div :class="['errorTip', {success: status === 'success' || status?.includes('check')}]">
            {{ $t('urlCheck.firewallTip') }}
          </div>
          <div class="ipWrapper">
            <span class="label ipLabel">{{ $t('urlCheck.sourceIp') }}
              <el-tooltip
                class="item"
                effect="dark"
                :visible-arrow="false"
                placement="top"
                popper-class="targetService-popper"
              >
                <div slot="content">
                  {{ $t('urlCheck.ipTooltip', { APP_TITLE: APP_TITLE }) }}
                </div>
                <i class="el-icon-question questionIcon"></i>
              </el-tooltip>
            </span>
            <clip-text class="ipContent">
              {{sourceIp.ipv4}}
            </clip-text>
            <clip-text>
              {{sourceIp.ipv6}}
            </clip-text>
            <el-button
              v-if="retry"
              style="margin-left: 20px"
              type="success"
              size="small"
              @click="retry"
            >{{ $t('urlCheck.retry') }}</el-button>
          </div>
        </div>
      </div>
    </el-col>
  </el-row>
</template>

<script>
import ClipText from '@/components/clip-text.vue';
import ProgressBar from '@/components/progress-bar.vue';
import { createNetworkDiagnosis, getNetworkDiagnosis, getWebsiteScreenshot } from '@/api/base';

const DefaultStageList = [
  {
    stageName: 'DNS',
    description: 'urlCheck.dnsResolution',
    status: 'NOT_STARTED',
  },
  {
    stageName: 'PORT',
    description: 'urlCheck.portStateCheck',
    status: 'NOT_STARTED',
  },
  {
    stageName: 'HTTP&HTTPS',
    description: 'urlCheck.httpHttpsCheck',
    status: 'NOT_STARTED',
  },
];

export default {
  name: 'UrlCheck',
  components: {
    ClipText,
    ProgressBar,
  },
  inject: ['sourceIp'],
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    skipScreenShot: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    retry: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      image: '',
      status: '',
      stageList: [...DefaultStageList],
      nextStage: 0,
    };
  },
  watch: {
    status() {
      this.emitChange();
    },
    value(val) {
      if (val === this.status) {
        return;
      }

      this.status = val;
    },
  },
  methods: {
    emitChange() {
      this.$emit('change', this.status);
    },
    getWebsiteScreenshot() {
      const { url } = this;

      if (!url) {
        return;
      }

      if (this.skipScreenShot) {
        return this.createNetworkDiagnosis();
      }

      this.status = 'connecting';

      return getWebsiteScreenshot({ sandboxUrl: url })
        .then((resp) => {
          if (!resp) {
            return Promise.reject();
          }

          const blob = new Blob([resp], { type: 'image/png' });
          this.image = URL.createObjectURL(blob);
          this.status = 'success';
        })
        .catch(this.createNetworkDiagnosis);
    },
    createNetworkDiagnosis() {
      this.stageList = [...DefaultStageList];
      this.nextStage = 0;

      if (this.skipScreenShot) {
        this.status = 'checking';
      } else {
        this.status = 'error';
      }

      return createNetworkDiagnosis({ testUrl: this.url })
        .then(({ id }) => this.getNetworkDiagnosis(id))
        .catch((error) => {
          console.log(error);
          this.status = 'error';
        });
    },
    getNetworkDiagnosis(id, params = {}) {
      return getNetworkDiagnosis(id, params, { custom: { manualHandleError: true } })
        .then((response) => {
          const { stages, status, nextStage } = response;

          this.stageList = stages.map((stage) => ({
            ...stage,
            description: this.$t(stage.description),
          }));
          this.nextStage = nextStage;

          if (status === 'FINISHED') {
            const allPass = stages.every((stage) => stage.status === 'PASS');

            this.nextStage = -1;
            if (this.skipScreenShot) {
              this.status = allPass ? 'checked' : 'error';
            }
            return;
          }

          return this.getNetworkDiagnosis(id);
        })
        .catch((error) => {
          const { errMsg } = error;

          this.status = 'error';

          const currStage = this.stageList.find((stage, index) => this.nextStage === index);
          this.nextStage = -1;

          if (currStage) {
            currStage.status = 'FAIL';
            currStage.resultInfo = errMsg;
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  @media screen and (max-width: 1440px) {
    .info {
      font-size: 14px;
    }
  }

  .waningText {
    display: inline-block;
    margin-right: 10px;
    background: url(~@assets/images/warning.png) no-repeat;
    background-size: cover;
    width: 20px;
    height: 20px;
  }

  .successTip {
    color: #e65808;
    margin: 20px 0;
    text-align: center;
  }

  .row {
    line-height: 40px;
  }

  .rowCenter {
    display: flex;
    align-items: center;
  }

  .label {
    font-weight: bold;
  }

  .placeholder {
    display: block;
    height: 20px;
  }

  .info {
    color: #909399;
  }

  .progress {
    margin-top: 20px;
    width: 80%;
    margin: 0 auto;
  }

  .infoWrapper {
    margin-top: 40px;
  }

  .textCenter {
    text-align: center;
  }

  .errorContent {
    margin: 0 auto;
  }

  .stageIcon {
    font-size: 20px;
    color: #909399;
  }

  .errorIcon {
    color: #f56c6c;
    font-size: 20px;
  }

  .successIcon {
    color: #67c23a;
    font-size: 20px;
  }

  .diagList {
    line-height: 2em;
  }

  .errorTip {
    color: #ff001f;
    margin-top: 20px;
    text-align: center;

    &.success {
      color: #909399;
    }
  }

  .ipWrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ipLabel {
    margin-right: 20px;
  }

  .ipContent {
    margin-right: 40px;
  }

  .successContent {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .el-icon-success {
      margin-right: 10px;
    }
  }

  .el-icon-success .check {
    font-size: 20px;
  }

  .ipText {
    margin-right: 20px;
    font-weight: bold;
  }

  .questionIcon {
    display: inline-block;
  }

  .imageWrapper {
    display: flex;
    justify-content: center;
  }
}
</style>